import { useEffect, useRef, useState, type ReactElement } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useParams } from "react-router-dom";
import Sidebar from "../../Sidebar";
import FeatureTutorialTrigger from "../../Tutorial/TutorialTrigger";
import { useTutorial } from "../../Tutorial/useTutorial";
import { useEnrichPatientMutation } from "../../../hooks/useEnrichPatientMutation";
import { useRecordInfoQuery } from "../../../hooks/useRecordInfoQuery";
import { isStandaloneUser as getIsStandaloneUser } from "../../../utils/standaloneUser";
import telehealthAudioManager from "../../../utils/TelehealthAudioManager";
import windowMessenger from "../../../utils/WindowMessenger.js";
import AudioRecorder from "../AudioRecorder";
import PatientSessionHeader from "./PatientSessionHeader";
import RecordPageFrame from "./RecordPageFrame";
import { resolvePatientId, shouldAttemptEnrichment } from "./recordPageRules";
import TelehealthSetupModal from "./TelehealthSetupModal";
import TelehealthToggle from "./TelehealthToggle";
import type { RecordPageLocationState, RecordPageProps } from "./types";
import { useTelehealthSession } from "./useTelehealthSession";

const RecordPage = ({ setAuth }: RecordPageProps): ReactElement => {
  const isStandaloneUser = getIsStandaloneUser();
  const location = useLocation();
  const { patientId: patientIdParam } = useParams();
  const locationState = (location.state ?? {}) as RecordPageLocationState;
  const patientId = resolvePatientId(patientIdParam, locationState.patientID);
  const hasCustomPromptHint = locationState.hasCustomPromptHint ?? null;
  const org = "VA";
  const doctorName = localStorage.getItem("userName");

  const [isAudio, setIsAudio] = useState(false);
  const [isNoteAccordionOpen, setIsNoteAccordionOpen] = useState(false);
  const [enrichedIdentifier, setEnrichedIdentifier] = useState<string | null>(null);
  const hasEnriched = useRef(false);

  const { data: recordInfo } = useRecordInfoQuery(patientId);
  const { mutate: enrichPatient, isPending: isEnriching } = useEnrichPatientMutation();
  const {
    isActive: isTutorialActive,
    currentStepId,
    goToStep,
    setPatientDescription,
  } = useTutorial();
  const patientName = recordInfo?.description ?? locationState.patientName ?? null;
  const isMockPatient = patientName === "QQQVAINTERFACE,IFC" && Boolean(isTutorialActive);
  const {
    isTelehealth,
    showTelehealthModal,
    audioState,
    handleTelehealthToggle,
    closeTelehealthModal,
    setupScreenAudio,
    handleTelehealthCleanup,
  } = useTelehealthSession({
    isRecording: isAudio,
    isTutorialActive,
    currentStepId,
    goToStep,
    audioManager: telehealthAudioManager,
    patientId,
    isMockPatient,
  });

  const mrn = recordInfo?.mrn ?? locationState.MRN ?? null;
  const userId = recordInfo?.user_id ?? locationState.userId;
  const identifier =
    enrichedIdentifier ?? recordInfo?.demographics ?? locationState.identifier ?? null;
  const patientRecord = recordInfo ?? locationState.patient ?? null;

  useEffect(() => {
    if (setPatientDescription) {
      setPatientDescription(patientName || null);
    }
  }, [patientName, setPatientDescription]);

  useEffect(() => {
    hasEnriched.current = false;
    setEnrichedIdentifier(null);
  }, [patientId]);

  useEffect(() => {
    if (!patientId) {
      return;
    }

    if (
      !shouldAttemptEnrichment({
        hasEnriched: hasEnriched.current,
        isStandalone: isStandaloneUser,
        hasAppointmentId: Boolean(recordInfo?.appointment_id),
      })
    ) {
      return;
    }

    hasEnriched.current = true;
    enrichPatient(
      { patientId, mrn, doctorName },
      {
        onSuccess: (result) => {
          if (!result.skipped && result.enriched && result.demographics) {
            setEnrichedIdentifier(result.demographics);
          }
        },
      }
    );
  }, [doctorName, enrichPatient, isStandaloneUser, mrn, patientId, recordInfo?.appointment_id]);

  useEffect(() => {
    if (userId) {
      windowMessenger.startKeepalive(userId, 300000);
    }

    return () => {
      windowMessenger.stopKeepalive();
    };
  }, [userId]);

  return (
    <>
      <Helmet>
        <title>Record Session – Knowtex</title>
      </Helmet>
      <Sidebar setAuth={setAuth} />
      <RecordPageFrame>
        <div className="flex-shrink-0 p-4 w-full" />
        <FeatureTutorialTrigger />

        <div
          data-testid="record-stage"
          className="flex-1 flex flex-col items-center justify-start px-4 pb-8"
          style={{
            marginLeft: isNoteAccordionOpen ? "50%" : "0",
            width: isNoteAccordionOpen ? "50%" : "100%",
            transition: "all 0.4s ease-in-out",
          }}
        >
          <PatientSessionHeader
            patientName={patientName}
            identifier={identifier}
            isEnriching={isEnriching}
          />

          {!isAudio ? (
            <TelehealthToggle
              isTelehealth={isTelehealth}
              isReady={Boolean(
                isTelehealth && audioState.hasMicrophone && audioState.hasScreenAudio
              )}
              onToggle={handleTelehealthToggle}
            />
          ) : null}

          <div className={`w-full mx-auto ${isNoteAccordionOpen ? "" : "max-w-[1400px]"}`}>
            <AudioRecorder
              patientID={patientId}
              doctorName={doctorName}
              setIsAudio={setIsAudio}
              isAudio={isAudio}
              mrn={mrn}
              patientName={patientName}
              org={org}
              identifier={identifier}
              patient={patientRecord}
              isTelehealth={isTelehealth}
              audioManager={telehealthAudioManager}
              hasTelehealthMicrophone={audioState.hasMicrophone}
              hasTelehealthScreenAudio={audioState.hasScreenAudio}
              onTelehealthCleanup={handleTelehealthCleanup}
              isNoteAccordionOpen={isNoteAccordionOpen}
              setIsNoteAccordionOpen={setIsNoteAccordionOpen}
              hasCustomPromptHint={hasCustomPromptHint}
            />
          </div>
        </div>
      </RecordPageFrame>

      <TelehealthSetupModal
        open={showTelehealthModal}
        audioState={audioState}
        onClose={closeTelehealthModal}
        onShareScreenAudio={setupScreenAudio}
        closeOnPressOutside={!isTutorialActive}
      />
    </>
  );
};

export default RecordPage;
